import { useState } from "react";
import type { IActivityTemplate } from "@/api/Templates";
import { ExpansionPanel } from "@medoma/components/ExpansionPanel/ExpansionPanel";
import styles from "./ActivityTemplate.module.scss";
import { HeadingWithEllipsis } from "@medoma/components/Heading/Heading";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import InvoicingBillIcon from "@medoma/components/icons/InvoicingBillIcon";
import {
  activityRequirementStatusSchema,
  type IActivityCategory,
} from "@medoma/models/activities";
import type { IMedicalCompetence } from "@medoma/models/shifts";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { CreateActivityFromTemplateForm } from "./CreateActivityFromTemplateForm/CreateActivityFromTemplateForm";

const Competences = ({
  competences,
}: {
  competences: IMedicalCompetence[];
}) => {
  return (
    <div className={styles.competences}>
      {competences.map((competence) => (
        <CompetenceChip
          key={competence}
          competence={competence}
          state={activityRequirementStatusSchema.enum.neutral}
        />
      ))}
    </div>
  );
};

const Header = ({
  category,
  title,
  requiredCompetences,
  hasInvoicingCodes,
}: {
  category: IActivityCategory;
  title: string;
  requiredCompetences: IMedicalCompetence[];
  hasInvoicingCodes: boolean;
}) => {
  return (
    <div className={styles.header}>
      <CategoryIcon category={category} size="small" />
      <div className={styles.titleAndInvoicingIcon}>
        <HeadingWithEllipsis
          level="h2"
          size="base"
          weight="regular"
          maxWidth="unset"
        >
          {title}
        </HeadingWithEllipsis>
        {hasInvoicingCodes ? <InvoicingBillIcon /> : null}
      </div>
      <Competences competences={requiredCompetences} />
    </div>
  );
};

const ActivityTemplate = ({ template }: { template: IActivityTemplate }) => {
  const displayedTitle = template.template.title;
  const [isExpanded, setIsExpanded] = useState(false);

  const hasInvoicingCodes =
    (template.template.procedureCodes?.length ?? 0) > 0 ||
    (template.template.productCodes?.length ?? 0) > 0;

  const handleSuccess = () => {
    setIsExpanded(false);
  };

  return (
    <ExpansionPanel
      trigger={
        <Header
          category={template.template.category}
          title={displayedTitle}
          requiredCompetences={template.template.requiredCompetences ?? []}
          hasInvoicingCodes={hasInvoicingCodes}
        />
      }
      triggerAriaLabel={displayedTitle}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
    >
      <div className={styles.content}>
        <CreateActivityFromTemplateForm
          template={template}
          onSuccess={handleSuccess}
        />
      </div>
    </ExpansionPanel>
  );
};

export { ActivityTemplate };
